import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { getOutlet, createReading, getReadings, updateReading } from '../api';
import { supabase } from '../supabaseClient';

function ReadingForm() {
  const [outlet, setOutlet] = useState(null);
  const [readingValue, setReadingValue] = useState('');
  const [remark, setRemark] = useState('');
  const [image, setImage] = useState(null);
  const [lastReading, setLastReading] = useState(null);
  const [userReadings, setUserReadings] = useState([]);
  const [editingReading, setEditingReading] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const { outletId } = useParams();
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchOutlet();
    fetchLastReading();
  }, [outletId]);

  const fetchOutlet = async () => {
    try {
      const response = await getOutlet(outletId);
      setOutlet(response.data);
    } catch (error) {
      console.error('Error fetching outlet:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLastReading = async () => {
    try {
      const response = await getReadings(outletId);
      if (response.data && response.data.length > 0) {
        setLastReading(response.data[0]);
        // Filter readings by current user
        const myReadings = response.data.filter(r => r.userId === user.id);
        setUserReadings(myReadings);
      }
    } catch (error) {
      console.error('Error fetching last reading:', error);
    }
  };

  const handleEditReading = (reading) => {
    setEditingReading({
      id: reading.id,
      readingValue: reading.readingValue,
      remark: reading.remark || ''
    });
  };

  const handleSaveEdit = async () => {
    try {
      await updateReading(editingReading.id, {
        readingValue: editingReading.readingValue,
        remark: editingReading.remark
      });
      setMessage({ type: 'success', text: '✓ Reading updated successfully!' });
      setEditingReading(null);
      fetchLastReading();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Failed to update reading' 
      });
    }
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      // Resize client-side before upload to save bandwidth and storage
      resizeImageFile(file, 1200, 1200, 0.7)
        .then((resized) => setImage(resized))
        .catch(() => setImage(file));
    }
  };

  // Resize an image file using a canvas and return a new File
  const resizeImageFile = (file, maxWidth = 1200, maxHeight = 1200, quality = 0.7) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        const aspect = width / height;
        if (width > maxWidth) {
          width = maxWidth;
          height = Math.round(width / aspect);
        }
        if (height > maxHeight) {
          height = maxHeight;
          width = Math.round(height * aspect);
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (!blob) return reject(new Error('Canvas is empty'));
            const resizedFile = new File([blob], file.name, { type: 'image/jpeg' });
            resolve(resizedFile);
          },
          'image/jpeg',
          quality
        );
      };
      img.onerror = (err) => reject(err);
      img.src = URL.createObjectURL(file);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
        // If we have a File object, upload to Supabase Storage first
        let imageUrl = null;
        if (image) {
          if (image instanceof File) {
            const ext = image.name.split('.').pop();
            const filename = `${outletId}_${Date.now()}.${ext}`;
            const { data: uploadData, error: uploadError } = await supabase.storage.from('readings').upload(filename, image, { cacheControl: '3600', upsert: false });
            if (uploadError) throw uploadError;
            const { data: publicData } = await supabase.storage.from('readings').getPublicUrl(filename);
            imageUrl = publicData?.publicUrl || publicData?.publicURL || null;
          } else if (typeof image === 'string') {
            imageUrl = image;
          }
        }

        await createReading({
          readingValue,
          outletId,
          imageUrl,
          remark
        });

      setMessage({ type: 'success', text: '✓ Reading submitted successfully!' });
      setReadingValue('');
      setRemark('');
      setImage(null);
      document.getElementById('imageInput').value = '';
      fetchLastReading();
      
      // Redirect after 2 seconds
      setTimeout(() => {
        handleBack();
      }, 2000);
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Failed to submit reading' 
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleBack = () => {
    if (outlet?.Utility?.id) {
      navigate(`/dashboard/utility/${outlet.Utility.id}`);
    } else {
      navigate('/dashboard');
    }
  };

  const calculateDifference = () => {
    if (lastReading && readingValue) {
      const diff = parseFloat(readingValue) - parseFloat(lastReading.readingValue);
      return diff.toFixed(2);
    }
    return null;
  };

  const difference = calculateDifference();

  if (loading) {
    return <div className="loading">Loading outlet information...</div>;
  }

  if (!outlet) {
    return (
      <div className="container">
        <div className="card">
          <div className="empty-state">
            <div className="empty-state-icon">⚠️</div>
            <h3>Outlet not found</h3>
            <p>The requested outlet could not be found</p>
            <button onClick={() => navigate('/dashboard')} className="btn btn-primary" style={{ marginTop: '16px' }}>
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="navbar">
        <h1>MeterBill Pro</h1>
        <div className="navbar-right">
          <span className="navbar-user">{user?.username}</span>
          <button onClick={handleLogout}>Logout</button>
        </div>
      </div>
      <div className="container">
        <button onClick={handleBack} className="btn btn-secondary" style={{ marginBottom: '24px' }}>
          ← Back
        </button>

        <div className="page-header">
          <h2>Submit Meter Reading</h2>
          <p>Enter the current meter reading value</p>
        </div>

        <div className="info-box">
          <div className="info-box-item">
            <span className="info-box-label">Utility:</span>
            <span className="info-box-value">{outlet.Utility?.name}</span>
          </div>
          <div className="info-box-item">
            <span className="info-box-label">Tenant:</span>
            <span className="info-box-value">{outlet.tenantName}</span>
          </div>
          <div className="info-box-item">
            <span className="info-box-label">Location:</span>
            <span className="info-box-value">{outlet.location}</span>
          </div>
          {outlet.meterNo && (
            <div className="info-box-item">
              <span className="info-box-label">Meter No:</span>
              <span className="info-box-value">{outlet.meterNo}</span>
            </div>
          )}
          <div className="info-box-item">
            <span className="info-box-label">Serial No:</span>
            <span className="info-box-value"><span className="badge badge-primary">{outlet.serialNo}</span></span>
          </div>
        </div>

        {lastReading && (
          <div className="card" style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, white 100%)', border: '1px solid #10b981' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <span style={{ fontSize: '24px' }}>✓</span>
              <div>
                <div style={{ fontWeight: '600', color: '#059669', fontSize: '15px' }}>
                  Last submission: {new Date(lastReading.createdAt).toLocaleString()}
                </div>
                <div style={{ color: '#6b7280', fontSize: '14px', marginTop: '4px' }}>
                  Previous reading: <strong>{lastReading.readingValue}</strong> by {lastReading.User?.username}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="card">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Meter Reading Value *</label>
              <input
                type="number"
                step="0.01"
                value={readingValue}
                onChange={(e) => setReadingValue(e.target.value)}
                required
                placeholder="Enter current meter reading"
                style={{ fontSize: '18px', fontWeight: '600' }}
              />
              {difference && (
                <div style={{ 
                  marginTop: '12px', 
                  padding: '12px', 
                  background: difference >= 0 ? '#f0fdf4' : '#fef2f2',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: difference >= 0 ? '#059669' : '#dc2626'
                }}>
                  Difference from last reading: {difference >= 0 ? '+' : ''}{difference}
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Remark (Optional)</label>
              <textarea
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                placeholder="Add any notes or remarks about this reading"
                rows="3"
                style={{ fontSize: '14px', resize: 'vertical' }}
              />
            </div>

            <div className="form-group">
              <label>Upload Image (Optional)</label>
              <input
                id="imageInput"
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                style={{ padding: '10px' }}
              />
              <small style={{ color: '#6b7280', display: 'block', marginTop: '8px' }}>
                📸 Upload a photo of the meter for reference
              </small>
            </div>

            {message.text && (
              <div className={message.type === 'success' ? 'success' : 'error'}>
                {message.text}
              </div>
            )}

            <button 
              type="submit" 
              className="btn btn-success" 
              disabled={submitting}
              style={{ marginTop: '24px', width: '100%', padding: '14px', fontSize: '16px' }}
            >
              {submitting ? 'Submitting...' : '✓ Submit Reading'}
            </button>
          </form>
        </div>

        {userReadings.length > 0 && (
          <div className="card" style={{ marginTop: '24px' }}>
            <h3 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: '600' }}>📜 Your Reading History</h3>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Reading</th>
                    <th>Remark</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {userReadings.map((reading) => (
                    <tr key={reading.id}>
                      <td style={{ fontSize: '13px' }}>
                        {new Date(reading.createdAt).toLocaleDateString()}<br/>
                        <small style={{ color: '#6b7280' }}>{new Date(reading.createdAt).toLocaleTimeString()}</small>
                      </td>
                      <td>
                        {editingReading && editingReading.id === reading.id ? (
                          <input
                            type="number"
                            step="0.01"
                            value={editingReading.readingValue}
                            onChange={(e) => setEditingReading({...editingReading, readingValue: e.target.value})}
                            style={{ width: '120px', padding: '6px' }}
                          />
                        ) : (
                          <strong style={{ fontSize: '16px' }}>{reading.readingValue}</strong>
                        )}
                      </td>
                      <td>
                        {editingReading && editingReading.id === reading.id ? (
                          <textarea
                            value={editingReading.remark}
                            onChange={(e) => setEditingReading({...editingReading, remark: e.target.value})}
                            rows="2"
                            style={{ width: '200px', padding: '6px', fontSize: '13px' }}
                          />
                        ) : (
                          reading.remark || '-'
                        )}
                      </td>
                      <td>
                        {editingReading && editingReading.id === reading.id ? (
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button
                              onClick={handleSaveEdit}
                              className="btn btn-success"
                              style={{ padding: '6px 12px', fontSize: '13px' }}
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setEditingReading(null)}
                              className="btn btn-secondary"
                              style={{ padding: '6px 12px', fontSize: '13px' }}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleEditReading(reading)}
                            className="btn btn-primary"
                            style={{ padding: '6px 12px', fontSize: '13px' }}
                          >
                            Edit
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ReadingForm;
